import React, { useEffect, useState } from "react";
import { DeliveryReturnApi } from "../../../api/DeliveryReturn/DeliveryReturnApi";

interface ReturnOrderModalProps {
  open: boolean;
  onClose: () => void;
  returnOrderId: number | null;
  onRefresh?: () => void;
}

interface ReturnOrderDetails {
  id: number;
  doId: number;
  status: string;
  reference: string;
  vendorName: string;
  sourceWarehouseName: string;
  destinationWarehouseName: string;
  deliveryWarehouseName: string;
  creationDate: string;
  returnLines: ReturnLineItem[];
}

interface ReturnLineItem {
  id: number;
  partId: number;
  partName: string;
  quantityReturned: number;
  returnReason: string;
}

const DeliveryReturnOrderModal: React.FC<ReturnOrderModalProps> = ({
  open,
  onClose,
  returnOrderId,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<ReturnOrderDetails | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (open && returnOrderId) {
      fetchReturnOrderDetails();
    }
  }, [open, returnOrderId]);

  const fetchReturnOrderDetails = async () => {
    if (!returnOrderId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await DeliveryReturnApi.api.getSingleReturnOrderDetails(
        returnOrderId
      );
      setOrderDetails(response.data);
    } catch (error) {
      console.error("Error fetching return order details:", error);
      setError("Failed to fetch return order details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!returnOrderId || !orderDetails) return;

    setCancelling(true);
    try {
      await DeliveryReturnApi.api.cancelDeliveryOrderReturn(returnOrderId);
      console.log("Return order cancelled successfully");
      onClose();
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error canceling return order", error);
      setError("Failed to cancel return order. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-5xl"
          style={{
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          }}>
          {/* Content */}
          <div className="p-8">
            {/* Header with Back Button */}
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={onClose}
                className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1 text-sm"
                style={{ color: "#646464" }}>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back
              </button>
            </div>

            <h2
              className="text-2xl font-semibold mb-6"
              style={{ fontWeight: "600", color: "#1E1E1E" }}>
              Return To Vendor
            </h2>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center h-64">
                <p className="text-red-500">{error}</p>
              </div>
            ) : !orderDetails ? (
              <div className="flex justify-center items-center h-64">
                <p className="text-gray-500">No return order details found</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Order Details Grid */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                  {/* ID */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">ID</p>
                    <div className="border-b border-gray-300 pb-2">
                      <p className="text-base">{orderDetails.id || "N/A"}</p>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Status</p>
                    <div className="border-b border-gray-300 pb-2">
                      <p className="text-base">
                        {orderDetails.status || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Reference */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Reference</p>
                    <div className="border-b border-gray-300 pb-2">
                      <p className="text-base">
                        {orderDetails.reference || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Vendor Name */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Vendor Name</p>
                    <div className="border-b border-gray-300 pb-2">
                      <p className="text-base">
                        {orderDetails.vendorName || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Delivery Warehouse Name */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">
                      Delivery Warehouse Name
                    </p>
                    <div className="border-b border-gray-300 pb-2">
                      <p className="text-base">
                        {orderDetails.deliveryWarehouseName || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Creation Date */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Creation Date</p>
                    <div className="border-b border-gray-300 pb-2">
                      <p className="text-base">
                        {orderDetails.creationDate
                          ? orderDetails.creationDate
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Products Section */}
                <div className="mt-8">
                  <h3
                    className="text-lg font-semibold mb-4"
                    style={{ fontWeight: "600" }}>
                    Return Line Items
                  </h3>
                  <div
                    className="bg-white rounded-lg overflow-hidden"
                    style={{ boxShadow: "0px 2px 6px rgba(0,0,0,0.05)" }}>
                    <table className="w-full">
                      <thead style={{ backgroundColor: "#F1F3F5" }}>
                        <tr>
                          <th className="text-left py-3 px-4 font-semibold text-sm">
                            ID
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">
                            Part ID
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">
                            Part Name
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">
                            Quantity Returned
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">
                            Return Reason
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetails.returnLines &&
                        orderDetails.returnLines.length > 0 ? (
                          orderDetails.returnLines.map((line) => (
                            <tr
                              key={line.id}
                              className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4 text-sm">
                                {line.id || "N/A"}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {line.partId || "N/A"}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {line.partName || "N/A"}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {line.quantityReturned || "N/A"}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {line.returnReason || "N/A"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={5}
                              className="py-8 text-center text-gray-500">
                              No return line items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer with Cancel Button */}
                {orderDetails?.status === "DRAFT" && (
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={handleCancelOrder}
                      disabled={cancelling}
                      className="px-6 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      style={{
                        borderRadius: "32px",
                        fontWeight: "500",
                        fontSize: "0.875rem",
                      }}>
                      {cancelling ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Cancelling...
                        </span>
                      ) : (
                        <span>Cancel Return</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryReturnOrderModal;
